import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Mic, MicOff, X, Volume2, VolumeX, Loader2, Bot, Zap } from "lucide-react";

/**
 * JARVIS — a voice-driven assistant panel.
 *
 *   • Wake word: say "Hey Jarvis" or "Jarvis" and the orb lights up and listens.
 *   • Tap-to-talk also works for anyone on a browser without wake-word recognition.
 *   • Backend `/api/jarvis/chat` runs GPT with tool-use for query / navigate /
 *     create / verify flows. Response is spoken back using the browser's
 *     en-GB voice (Daniel on Apple, Google UK on Chrome/Android).
 */

const BRITISH_VOICE_HINTS = ["daniel", "google uk english male", "oliver", "arthur"];
const WAKE_WORDS = ["hey jarvis", "jarvis", "hey jervis"];

const pickVoice = () => {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices?.length) return null;
  // Prefer a known British male voice
  for (const hint of BRITISH_VOICE_HINTS) {
    const v = voices.find((x) => x.name.toLowerCase().includes(hint));
    if (v) return v;
  }
  // Fall back to any en-GB voice
  const gb = voices.find((v) => (v.lang || "").toLowerCase().startsWith("en-gb"));
  if (gb) return gb;
  return voices[0];
};

const speak = async (text, { onEnd, enabled, audioRef, apiBase, token } = {}) => {
  if (!enabled) { onEnd?.(); return; }
  // Try ElevenLabs via backend proxy first
  if (apiBase) {
    try {
      const res = await fetch(`${apiBase}/api/jarvis/speak`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text }),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        if (audioRef) audioRef.current = audio;
        audio.onended = () => { URL.revokeObjectURL(url); onEnd?.(); };
        audio.onerror = () => { URL.revokeObjectURL(url); onEnd?.(); };
        await audio.play();
        return;
      }
      // 503 means key not set — fall back silently
      if (res.status !== 503) {
        /* non-fatal — fall back */
      }
    } catch {
      /* network issue — fall back */
    }
  }
  // Browser fallback (Web Speech)
  if (typeof window === "undefined" || !window.speechSynthesis) { onEnd?.(); return; }
  const voice = pickVoice();
  const u = new SpeechSynthesisUtterance(text);
  if (voice) u.voice = voice;
  u.lang = "en-GB";
  u.rate = 1.02;
  u.pitch = 0.95;
  u.onend = () => onEnd?.();
  u.onerror = () => onEnd?.();
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
};

export default function JarvisAssistant() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [awake, setAwake] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [messages, setMessages] = useState([]); // {role, text, result?}
  const [interim, setInterim] = useState("");
  const [supported, setSupported] = useState(true);
  const [permError, setPermError] = useState("");
  const [textInput, setTextInput] = useState("");
  const recognitionRef = useRef(null);
  const historyRef = useRef([]);
  const awakeRef = useRef(false);
  const openRef = useRef(false);
  const audioRef = useRef(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const getToken = () => { try { return localStorage.getItem("jc_token"); } catch { return null; } };

  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { awakeRef.current = awake; }, [awake]);

  const stopRecognition = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    setListening(false);
  }, []);

  const sendTurn = useCallback(async (userTurn) => {
    const text = (userTurn || "").trim();
    if (!text) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInterim("");
    setThinking(true);
    try {
      const { data } = await api.post("/jarvis/chat", {
        history: historyRef.current.slice(-8),
        user_turn: text,
        current_path: location.pathname,
      }, { timeout: 60000 });
      historyRef.current.push({ role: "user", text });
      historyRef.current.push({ role: "assistant", text: data.speak });
      setMessages((m) => [...m, { role: "assistant", text: data.speak, result: data.tool_result, tool: data.tool }]);
      // Side-effects
      if (data.navigate_to) {
        setTimeout(() => navigate(data.navigate_to), 400);
      }
      // Speak the response. Keep awake flag off while speaking to avoid loops.
      awakeRef.current = false;
      setAwake(false);
      await speak(data.speak, {
        enabled: voiceEnabled,
        audioRef,
        apiBase,
        token: getToken(),
        onEnd: () => {
          // Resume background listening after speech ends
          if (openRef.current && supported) startBackgroundListening();
        },
      });
    } catch (err) {
      const msg = err.response?.data?.detail || "I'm afraid I lost signal, sir.";
      setMessages((m) => [...m, { role: "assistant", text: msg, error: true }]);
      speak(msg, { enabled: voiceEnabled, audioRef, apiBase, token: getToken() });
    } finally {
      setThinking(false);
    }
  }, [location.pathname, navigate, supported, voiceEnabled, apiBase]);

  const handleResult = useCallback((event) => {
    let interimText = "";
    let finalText = "";
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const r = event.results[i];
      if (r.isFinal) finalText += r[0].transcript;
      else interimText += r[0].transcript;
    }
    const lower = (interimText + " " + finalText).toLowerCase();
    setInterim(interimText);

    // Barge-in: if Jarvis is currently speaking and the user starts talking,
    // cut him off immediately so the conversation feels real.
    if ((interimText || finalText).trim()) {
      if (audioRef.current && !audioRef.current.paused) {
        try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch {}
      }
      if (window.speechSynthesis?.speaking) {
        try { window.speechSynthesis.cancel(); } catch {}
      }
    }

    // When the panel is already open, treat every final utterance as a question
    // — no wake word required. The wake word only matters when the panel is
    // closed (to summon Jarvis in the first place).
    if (openRef.current) {
      if (finalText.trim()) {
        let utterance = finalText.trim();
        for (const w of WAKE_WORDS) {
          const re = new RegExp(`^\\s*${w}[,.\\s]*`, "i");
          utterance = utterance.replace(re, "");
        }
        if (utterance) sendTurn(utterance);
      }
      return;
    }

    // Panel closed → listen for wake word to open & greet
    const wakeHit = WAKE_WORDS.some((w) => lower.includes(w));
    if (wakeHit) {
      awakeRef.current = true;
      setAwake(true);
      setOpen(true);
      // Soft beep for wake acknowledgement
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = 1200;
        g.gain.value = 0.0001;
        o.connect(g); g.connect(ctx.destination);
        const now = ctx.currentTime;
        g.gain.exponentialRampToValueAtTime(0.2, now + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
        o.start(now); o.stop(now + 0.2);
      } catch { /* noop */ }
    }
  }, [sendTurn]);

  const startBackgroundListening = useCallback(async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    // Explicitly request microphone permission first so the user sees the
    // browser prompt and we can give a clear error if it is denied.
    if (navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // We don't need the stream itself — SpeechRecognition will use its own.
        stream.getTracks().forEach((t) => t.stop());
      } catch (err) {
        setPermError(
          "Microphone blocked. Click the lock icon in your address bar → Site settings → Allow Microphone, then reopen Jarvis."
        );
        setListening(false);
        return;
      }
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* noop */ }
    }
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = true;
    rec.onresult = handleResult;
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setPermError(
          "Microphone blocked. Click the lock icon in your address bar → Site settings → Allow Microphone, then reopen Jarvis."
        );
        setListening(false);
        return;
      }
      // Transient errors — restart on end
    };
    rec.onend = () => {
      // Auto-restart as long as the panel is still open and no permission error
      if (openRef.current && !permError) {
        try { rec.start(); } catch { /* already running */ }
      } else {
        setListening(false);
      }
    };
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
      setPermError("");
    } catch (e) {
      // start() throws if already started — that's fine
    }
  }, [handleResult, permError]);

  // Start listening when panel opens, stop when it closes
  useEffect(() => {
    if (open) {
      // Load voices (some browsers hydrate async)
      if (window.speechSynthesis) window.speechSynthesis.getVoices();
      startBackgroundListening();
    } else {
      stopRecognition();
      setAwake(false);
      setInterim("");
    }
    return () => stopRecognition();
  }, [open, startBackgroundListening, stopRecognition]);

  const toggleOpen = async () => {
    if (!open) {
      setOpen(true);
      // On first open, request a real briefing from the backend (overtime,
      // pending approvals, low stock, etc.) instead of a generic hello.
      if (messages.length === 0) {
        const placeholder = "Standing by, sir. Pulling the morning rundown…";
        setMessages([{ role: "assistant", text: placeholder }]);
        setThinking(true);
        try {
          const { data } = await api.get("/jarvis/briefing", { timeout: 20000 });
          const text = (data?.speak || "").trim() || "At your service, sir.";
          setMessages([{ role: "assistant", text }]);
          historyRef.current.push({ role: "assistant", text });
          speak(text, { enabled: voiceEnabled, audioRef, apiBase, token: getToken() });
        } catch (err) {
          const fallback = "Good day, sir. I'm here — speak or type what you need.";
          setMessages([{ role: "assistant", text: fallback }]);
          speak(fallback, { enabled: voiceEnabled, audioRef, apiBase, token: getToken() });
        } finally {
          setThinking(false);
        }
      }
    } else {
      setOpen(false);
    }
  };

  const manualAsk = () => {
    // Treat the current interim as a submission when the user taps the mic
    awakeRef.current = true;
    setAwake(true);
    toast.info("Listening — speak now", { duration: 1800 });
  };

  // Simple result renderer — show up to a few rows
  const renderResult = (m) => {
    const r = m.result;
    if (!r || typeof r !== "object") return null;
    if (Array.isArray(r.deliveries)) {
      return (
        <ul className="mt-2 space-y-1">
          {r.deliveries.slice(0, 6).map((d) => (
            <li key={d.id} className="text-[11px] font-mono text-cyan-200 bg-cyan-900/20 border border-cyan-500/30 rounded px-2 py-1">
              {d.reference || "no-ref"} · {d.project} · {d.received}/{d.expected} · <span className="text-cyan-400">{d.status}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (Array.isArray(r.projects)) {
      return (
        <ul className="mt-2 space-y-1">
          {r.projects.slice(0, 6).map((p) => (
            <li key={p.id} className="text-[11px] font-mono text-cyan-200 bg-cyan-900/20 border border-cyan-500/30 rounded px-2 py-1">
              {p.name} · {p.client || "—"} · <span className="text-cyan-400">{p.status}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (Array.isArray(r.items)) {
      return (
        <ul className="mt-2 space-y-1">
          {r.items.slice(0, 6).map((i, idx) => (
            <li key={idx} className="text-[11px] font-mono text-cyan-200 bg-cyan-900/20 border border-cyan-500/30 rounded px-2 py-1">
              {i.sku} · {i.name} · qty {i.quantity ?? "?"}
            </li>
          ))}
        </ul>
      );
    }
    if (r.lines) {
      return (
        <ul className="mt-2 space-y-1">
          {r.lines.slice(0, 6).map((l, idx) => (
            <li key={idx} className="text-[11px] font-mono text-cyan-200 bg-cyan-900/20 border border-cyan-500/30 rounded px-2 py-1">
              {l.sku} · {l.name} · {l.received}/{l.expected} · <span className="text-cyan-400">{l.status}</span>
            </li>
          ))}
        </ul>
      );
    }
    return null;
  };

  return (
    <>
      {/* Orb */}
      <button
        type="button"
        onClick={toggleOpen}
        aria-label="Open Jarvis"
        data-testid="jarvis-orb"
        className={`fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full grid place-items-center shadow-[0_0_30px_rgba(34,211,238,0.7)] border-2 border-cyan-400/60 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-900 text-cyan-300 hover:scale-105 transition-transform no-print ${open ? "ring-2 ring-cyan-300" : ""} ${awake ? "animate-pulse" : ""}`}
      >
        <Bot size={24} />
        {listening && (
          <span className="absolute inset-0 rounded-full animate-ping border-2 border-cyan-400/40 pointer-events-none" />
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-20 right-5 z-[60] w-[min(400px,calc(100vw-24px))] max-h-[min(500px,55vh)] rounded-md border border-cyan-500/40 bg-slate-950/95 text-cyan-100 shadow-[0_0_40px_rgba(34,211,238,0.35)] overflow-hidden no-print font-mono flex flex-col"
          data-testid="jarvis-panel"
        >
          {/* Header */}
          <div className="px-3 py-2 border-b border-cyan-500/40 bg-gradient-to-r from-slate-900 to-slate-950 flex items-center gap-2">
            <div className={`h-2 w-2 rounded-full ${awake ? "bg-cyan-300 animate-pulse" : "bg-cyan-700"}`} />
            <div className="font-display text-sm font-bold tracking-widest uppercase text-cyan-200">
              JARVIS
            </div>
            <span className="text-[9px] uppercase tracking-widest text-cyan-500 ml-1">
              {thinking ? "thinking…" : listening ? "listening · just talk" : "panel open · type below"}
            </span>
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() => setVoiceEnabled((v) => !v)}
                className="text-cyan-300 hover:text-white p-1"
                aria-label={voiceEnabled ? "Mute Jarvis" : "Unmute Jarvis"}
                data-testid="jarvis-voice-toggle"
              >
                {voiceEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
              </button>
              <button
                type="button"
                onClick={manualAsk}
                className="text-cyan-300 hover:text-white p-1"
                aria-label="Push to talk"
                data-testid="jarvis-mic-btn"
              >
                {listening ? <Mic size={14} /> : <MicOff size={14} />}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-cyan-300 hover:text-white p-1"
                aria-label="Close Jarvis"
                data-testid="jarvis-close-btn"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Transcript */}
          <div className="p-3 space-y-2 overflow-y-auto flex-1 min-h-0 text-[12px] leading-snug">
            {!supported && (
              <div className="text-amber-400 text-[11px] border border-amber-500/40 bg-amber-500/10 rounded px-2 py-1">
                Your browser doesn't support voice recognition. Type in the box below or use Chrome, Edge, or Safari.
              </div>
            )}
            {permError && (
              <div className="text-red-300 text-[11px] border border-red-500/40 bg-red-500/10 rounded px-2 py-2 space-y-2">
                <div className="font-bold uppercase tracking-widest text-[10px] text-red-300">Microphone blocked</div>
                <div className="text-red-200">{permError}</div>
                <button
                  type="button"
                  onClick={() => { setPermError(""); startBackgroundListening(); }}
                  data-testid="jarvis-retry-mic"
                  className="w-full py-1.5 rounded-sm bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/40 text-cyan-100 text-[11px] font-bold uppercase tracking-widest"
                >
                  Try Again
                </button>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                <div className={`inline-block max-w-full px-2.5 py-1.5 rounded ${
                  m.role === "user"
                    ? "bg-cyan-500/15 border border-cyan-400/40 text-cyan-100"
                    : m.error
                    ? "bg-red-500/15 border border-red-400/40 text-red-200"
                    : "bg-slate-800/80 border border-cyan-500/20 text-cyan-100"
                }`}>
                  {m.text}
                  {renderResult(m)}
                </div>
              </div>
            ))}
            {interim && (
              <div className="text-right">
                <div className="inline-block px-2.5 py-1.5 rounded bg-cyan-500/5 border border-cyan-400/20 text-cyan-400/80 italic">
                  {interim}…
                </div>
              </div>
            )}
            {thinking && (
              <div className="flex items-center gap-2 text-cyan-400 text-[11px]">
                <Loader2 size={12} className="animate-spin" /> Jarvis is thinking…
              </div>
            )}
          </div>

          {/* Text input fallback — always available */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const t = textInput.trim();
              if (!t || thinking) return;
              setTextInput("");
              sendTurn(t);
            }}
            className="px-3 py-2 border-t border-cyan-500/30 bg-slate-900/70 flex items-center gap-2"
            data-testid="jarvis-input-form"
          >
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Type a question or just talk…"
              aria-label="Ask Jarvis"
              data-testid="jarvis-text-input"
              className="flex-1 bg-slate-950/80 border border-cyan-500/30 rounded-sm px-2 py-1.5 text-[12px] text-cyan-100 placeholder:text-cyan-700 focus:outline-none focus:border-cyan-400 font-mono"
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={!textInput.trim() || thinking}
              data-testid="jarvis-send-btn"
              className="px-3 py-1.5 rounded-sm text-[11px] font-bold uppercase tracking-widest bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/40 text-cyan-100 disabled:opacity-40"
            >
              Send
            </button>
          </form>

          <div className="px-3 py-1.5 border-t border-cyan-500/30 bg-slate-900/70 text-[9px] uppercase tracking-widest text-cyan-500 flex items-center gap-2">
            <Zap size={9} /> {voiceEnabled ? "voice on" : "voice muted"} · {listening ? "mic live — just talk" : permError ? "mic blocked" : "type or allow mic"}
          </div>
        </div>
      )}
    </>
  );
}
